/**
 * ████████╗ ██████╗ ██╗     ██╗███╗   ██╗ ██████╗
 * ╚══██╔══╝██╔═══██╗██║     ██║████╗  ██║██╔═══██╗
 *    ██║   ██║   ██║██║     ██║██╔██╗ ██║██║   ██║
 *    ██║   ██║   ██║██║     ██║██║╚██╗██║██║   ██║
 *    ██║   ╚██████╔╝███████╗██║██║ ╚████║╚██████╔╝
 *    ╚═╝    ╚═════╝ ╚══════╝╚═╝╚═╝  ╚═══╝ ╚═════╝
 *
 * (c) Copyright 2021-present Rakuten Kobo Inc. (https://www.kobo.com)
 */

// -----------------------------------------------------------------------------
// IMPORTS
// -----------------------------------------------------------------------------

// External/Third-party dependencies
import React from 'react';


// -----------------------------------------------------------------------------
// CONFIGURATION
// -----------------------------------------------------------------------------

/**
 * The default state of the hook, i.e. no error
 * @type {object}
 * @default
 */
const DEFAULT_STATE = {
  hasError: false,
  error: undefined
};


// -----------------------------------------------------------------------------
// ERROR STATE HOOK
// -----------------------------------------------------------------------------

/**
 * A hook to handle error state of, for example, a component. The error state
 * consists of two properties:
 * * `hasError`: Whether or not the state is errornous
 * * `error`: An object of - or inherited from - prototype `Error`
 *
 * @example
 *  function MyComponent() {
 *    const [ errorState, setErrorState ] = useErrorState();
 *    // ...
 *
 *    // Something went wrong
 *    setErrorState(new CustomError({ message: 'Some error message' }));
 *
 *    return errorState.hasError
 *      ? <Text>That did not work</Text>
 *      : <Text>All good</Text>;
 *  }
 *
 * @return {Array} Error state:
 *  * The current error state
 *  * Function to set the error state
 *  * Function to reset the error state
 */
function useErrorState() {
  const [ _errorState, _setErrorState ] = React.useState(DEFAULT_STATE);

  /**
   * Stores the current error in the state of the hook
   *
   * @param {Error} error The (custom) error instance this error state represents
   */
  function setErrorState(error) {
    _setErrorState({
      hasError: true,
      error
    });
  }

  /**
   * Reset the current error state
   */
  function resetErrorState() {
    _setErrorState(DEFAULT_STATE);
  }

  return [ _errorState, setErrorState, resetErrorState ];
}


// -----------------------------------------------------------------------------
// EXPORTS
// -----------------------------------------------------------------------------

export default useErrorState;
